import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChevronLeft,
  CheckCircle,
  Loader2,
  Pencil,
  Settings,
  AlertCircle,
  FileText,
  List,
  StickyNote,
} from 'lucide-react'
import { Button, Card, CardContent, Input } from '@/components/ui'
import { ExportMenu, ChapterList, NotesTab } from '@/components/studio'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import { useStudio } from '@/context/StudioContext'
import { useWritingStudio } from '@/context/WritingStudioContext'
import { StudioModeIndicator } from '@/components/StudioModeIndicator'
import type { ProjectWithChapters } from '@/types/writingStudio'

// Autosave debounce time in ms
const AUTOSAVE_DELAY = 1500

// Export reminder threshold (7 days in ms)
const EXPORT_REMINDER_DAYS = 7
const EXPORT_REMINDER_MS = EXPORT_REMINDER_DAYS * 24 * 60 * 60 * 1000

export function ProjectEditor() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()
  const { mode } = useStudio()
  const { repository, readOnly, storageHealth, dismissStorageError, refreshStorageHealth } = useWritingStudio()

  // Project state
  const [projectData, setProjectData] = useState<ProjectWithChapters | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // UI state
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleInput, setTitleInput] = useState('')
  const [showExportReminder, setShowExportReminder] = useState(false)

  // Mobile tab state
  const [mobileTab, setMobileTab] = useState<'write' | 'chapters' | 'notes'>('write')

  // Selected chapter - local state that syncs with project.activeChapterId
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null)

  // Refs
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Get active chapter - use selectedChapterId (local) or fall back to project.activeChapterId
  const activeChapter = projectData?.chapters.find(
    (c) => c.id === (selectedChapterId || projectData.project.activeChapterId)
  ) || projectData?.chapters[0]

  // Sort chapters by order for display
  const sortedChapters = projectData?.chapters.slice().sort((a, b) => a.order - b.order) ?? []

  // Load project
  useEffect(() => {
    async function loadProject() {
      if (!projectId || !activeWriter) return

      try {
        // Handle 'new' project - create one first
        if (projectId === 'new') {
          const createResult = await repository.createProject(activeWriter.id)
          if (createResult.success) {
            navigate(`/studio/write/${createResult.data.project.id}`, { replace: true })
            return
          } else {
            showToast(createResult.error || 'Could not create project', 'error')
            navigate('/studio/write')
            return
          }
        }

        // Load existing project
        const result = await repository.getProjectWithChapters(projectId)
        if (result.success && result.data) {
          setProjectData(result.data)
          setTitleInput(result.data.project.title)
          setLastSaved(new Date(result.data.project.updatedAt))
        } else {
          showToast('Project not found', 'error')
          navigate('/studio/write')
        }
      } catch (err) {
        console.error('Failed to load project:', err)
        showToast('Could not load project', 'error')
        navigate('/studio/write')
      } finally {
        setIsLoading(false)
      }
    }

    loadProject()
  }, [projectId, activeWriter, navigate, showToast, repository])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // Focus textarea on load
  useEffect(() => {
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [isLoading])

  // Sync selectedChapterId when project loads
  useEffect(() => {
    if (projectData && !selectedChapterId) {
      setSelectedChapterId(projectData.project.activeChapterId || projectData.chapters[0]?.id || null)
    }
  }, [projectData, selectedChapterId])

  // Check if export reminder should show
  useEffect(() => {
    if (!projectData) return

    const { lastExportedAt, createdAt, wordCount } = projectData.project

    // Only show reminder if project has content
    if (wordCount === 0) {
      setShowExportReminder(false)
      return
    }

    const now = Date.now()
    const referenceDate = lastExportedAt ? new Date(lastExportedAt).getTime() : new Date(createdAt).getTime()
    const daysSinceReference = now - referenceDate

    setShowExportReminder(daysSinceReference > EXPORT_REMINDER_MS)
  }, [projectData])

  // Save chapter content with collision detection
  const saveChapterContent = useCallback(
    async (chapterId: string, content: string, expectedUpdatedAt?: string) => {
      if (!projectData || readOnly) {
        if (readOnly) {
          showToast('Your subscription has lapsed. Renew to save changes.', 'error')
        }
        return
      }

      setIsSaving(true)
      try {
        const result = await repository.updateChapter(chapterId, {
          content,
          expectedUpdatedAt,
        })
        if (result.success) {
          // Update local state with new chapter data
          setProjectData((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              chapters: prev.chapters.map((c) =>
                c.id === chapterId ? result.data : c
              ),
              project: {
                ...prev.project,
                wordCount: prev.chapters.reduce(
                  (sum, c) => sum + (c.id === chapterId ? result.data.wordCount : c.wordCount),
                  0
                ),
                updatedAt: result.data.updatedAt,
              },
            }
          })
          setLastSaved(new Date())
          setHasUnsavedChanges(false)
        } else {
          // Handle conflict by reloading to get fresh data
          if (result.code === 'CONFLICT') {
            showToast('Content was modified in another tab. Refreshing...', 'error')
            // Reload to get fresh data after a short delay
            setTimeout(() => window.location.reload(), 1500)
          } else {
            showToast(result.error || 'Could not save. Please try again.', 'error')
            refreshStorageHealth()
          }
        }
      } catch (err) {
        console.error('Failed to save:', err)
        showToast('Could not save. Please try again.', 'error')
        refreshStorageHealth()
      } finally {
        setIsSaving(false)
      }
    },
    [projectData, repository, readOnly, showToast, refreshStorageHealth]
  )

  // Handle content change with debounced autosave
  const handleContentChange = (content: string) => {
    if (!projectData || !activeChapter) return

    // Update local state immediately for responsive UI
    setProjectData((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        chapters: prev.chapters.map((c) =>
          c.id === activeChapter.id
            ? { ...c, content, wordCount: content.trim().split(/\s+/).filter(Boolean).length }
            : c
        ),
      }
    })
    setHasUnsavedChanges(true)

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Set new timeout for autosave
    // Pass expected updatedAt for collision detection
    const expectedUpdatedAt = activeChapter.updatedAt
    saveTimeoutRef.current = setTimeout(() => {
      saveChapterContent(activeChapter.id, content, expectedUpdatedAt)
    }, AUTOSAVE_DELAY)
  }

  // Switch to a different chapter
  const handleChapterSelect = useCallback(
    async (chapterId: string) => {
      if (!projectData || chapterId === selectedChapterId) return

      // Flush any pending save first
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
        // Save current content immediately if there are unsaved changes
        if (hasUnsavedChanges && activeChapter) {
          await saveChapterContent(activeChapter.id, activeChapter.content, activeChapter.updatedAt)
        }
      }

      // Update local selection
      setSelectedChapterId(chapterId)
      setMobileTab('write') // Switch to write tab on mobile after selecting

      // Persist active chapter to project
      try {
        await repository.updateProject(projectData.project.id, {
          activeChapterId: chapterId,
        })
        setProjectData((prev) =>
          prev ? { ...prev, project: { ...prev.project, activeChapterId: chapterId } } : prev
        )
      } catch (err) {
        console.error('Failed to update active chapter:', err)
      }

      // Focus textarea after switching
      setTimeout(() => textareaRef.current?.focus(), 50)
    },
    [projectData, selectedChapterId, hasUnsavedChanges, activeChapter, saveChapterContent, repository]
  )

  // Add a new chapter
  const handleAddChapter = useCallback(async () => {
    if (!projectData || readOnly) {
      if (readOnly) {
        showToast('Your subscription has lapsed. Renew to add chapters.', 'error')
      }
      return
    }

    try {
      const result = await repository.createChapter(projectData.project.id)
      if (result.success) {
        // Add new chapter to local state
        setProjectData((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            chapters: [...prev.chapters, result.data],
            project: {
              ...prev.project,
              activeChapterId: result.data.id,
              updatedAt: result.data.updatedAt,
            },
          }
        })
        setSelectedChapterId(result.data.id)
        setMobileTab('write') // Switch to write tab on mobile
        showToast('New chapter created!', 'success')
        setTimeout(() => textareaRef.current?.focus(), 50)
      } else {
        showToast(result.error || 'Could not create chapter', 'error')
      }
    } catch (err) {
      console.error('Failed to create chapter:', err)
      showToast('Could not create chapter', 'error')
    }
  }, [projectData, readOnly, repository, showToast])

  // Rename a chapter
  const handleRenameChapter = useCallback(
    async (chapterId: string, newTitle: string) => {
      if (!projectData || readOnly) return

      try {
        const result = await repository.updateChapter(chapterId, { title: newTitle })
        if (result.success) {
          setProjectData((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              chapters: prev.chapters.map((c) =>
                c.id === chapterId ? { ...c, title: result.data.title } : c
              ),
            }
          })
        } else {
          showToast(result.error || 'Could not rename chapter', 'error')
        }
      } catch (err) {
        console.error('Failed to rename chapter:', err)
        showToast('Could not rename chapter', 'error')
      }
    },
    [projectData, readOnly, repository, showToast]
  )

  // Delete a chapter
  const handleDeleteChapter = useCallback(
    async (chapterId: string) => {
      if (!projectData || readOnly) return
      if (projectData.chapters.length <= 1) {
        showToast('Cannot delete the only chapter', 'error')
        return
      }

      try {
        const result = await repository.deleteChapter(chapterId)
        if (result.success) {
          // If deleting the selected chapter, switch to another
          const remainingChapters = projectData.chapters.filter((c) => c.id !== chapterId)
          const newSelectedId = selectedChapterId === chapterId
            ? remainingChapters[0]?.id || null
            : selectedChapterId

          setProjectData((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              chapters: remainingChapters,
              project: {
                ...prev.project,
                activeChapterId: newSelectedId,
                wordCount: remainingChapters.reduce((sum, c) => sum + c.wordCount, 0),
              },
            }
          })

          if (selectedChapterId === chapterId) {
            setSelectedChapterId(newSelectedId)
          }

          showToast('Chapter deleted', 'success')
        } else {
          showToast(result.error || 'Could not delete chapter', 'error')
        }
      } catch (err) {
        console.error('Failed to delete chapter:', err)
        showToast('Could not delete chapter', 'error')
      }
    },
    [projectData, readOnly, repository, selectedChapterId, showToast]
  )

  // Reorder chapters
  const handleReorderChapters = useCallback(
    async (orderedIds: string[]) => {
      if (!projectData || readOnly) return

      try {
        const result = await repository.reorderChapters(projectData.project.id, orderedIds)
        if (result.success) {
          setProjectData((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              chapters: result.data,
            }
          })
        } else {
          showToast(result.error || 'Could not reorder chapters', 'error')
        }
      } catch (err) {
        console.error('Failed to reorder chapters:', err)
        showToast('Could not reorder chapters', 'error')
      }
    },
    [projectData, readOnly, repository, showToast]
  )

  // Save notes
  const handleSaveNotes = useCallback(
    async (content: string) => {
      if (!projectData || readOnly) return

      try {
        const result = await repository.updateNotes(projectData.project.id, content)
        if (result.success) {
          setProjectData((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              notes: content,
            }
          })
        } else {
          showToast(result.error || 'Could not save notes', 'error')
        }
      } catch (err) {
        console.error('Failed to save notes:', err)
        showToast('Could not save notes', 'error')
      }
    },
    [projectData, readOnly, repository, showToast]
  )

  // Handle title save
  const handleTitleSave = async () => {
    if (!projectData || !titleInput.trim() || readOnly) {
      if (readOnly) {
        showToast('Your subscription has lapsed. Renew to make changes.', 'error')
      }
      return
    }

    try {
      const result = await repository.updateProject(projectData.project.id, {
        title: titleInput.trim(),
      })
      if (result.success) {
        setProjectData((prev) =>
          prev ? { ...prev, project: result.data } : prev
        )
        setIsEditingTitle(false)
        showToast('Title updated!', 'success')
      } else {
        showToast(result.error || 'Could not update title', 'error')
      }
    } catch (err) {
      console.error('Failed to update title:', err)
      showToast('Could not update title', 'error')
    }
  }

  // Handle export completion - record the timestamp
  const handleExportComplete = useCallback(async () => {
    if (!projectData) return

    try {
      const result = await repository.recordExport(projectData.project.id)
      if (result.success) {
        // Update local state to reflect the export
        setProjectData((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            project: {
              ...prev.project,
              lastExportedAt: new Date().toISOString(),
            },
          }
        })
        setShowExportReminder(false)
        showToast('Export complete!', 'success')
      }
    } catch (err) {
      console.error('Failed to record export:', err)
    }
  }, [projectData, repository, showToast])

  // Format last saved time
  const formatLastSaved = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Calculate total word count
  const totalWordCount = projectData?.chapters.reduce((sum, c) => sum + c.wordCount, 0) ?? 0

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
      </div>
    )
  }

  if (!projectData || !activeChapter) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <p className="text-[var(--color-kids-text-muted)]">Project not found</p>
      </div>
    )
  }

  const { project } = projectData

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm flex-shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link
              to="/studio/write"
              className="flex items-center gap-2 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">My Projects</span>
            </Link>

            <span className="text-[var(--color-kids-border)]">|</span>

            {/* Project title */}
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  className="w-64"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave()
                    if (e.key === 'Escape') {
                      setTitleInput(project.title)
                      setIsEditingTitle(false)
                    }
                  }}
                />
                <Button size="sm" onClick={handleTitleSave}>
                  Save
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingTitle(true)}
                className="flex items-center gap-2 text-[var(--color-kids-text)] hover:text-[var(--color-kids-primary)] transition-colors"
              >
                <span className="text-2xl">{project.coverEmoji}</span>
                <span className="font-display font-bold">{project.title}</span>
                <Pencil className="w-4 h-4 opacity-50" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Studio mode indicator */}
            <StudioModeIndicator size="sm" />

            {/* Save indicator */}
            <div className="flex items-center gap-2 text-sm">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-[var(--color-kids-text-muted)]" />
                  <span className="text-[var(--color-kids-text-muted)]">Saving...</span>
                </>
              ) : hasUnsavedChanges ? (
                <span className="text-[var(--color-kids-text-muted)]">Unsaved changes</span>
              ) : lastSaved ? (
                <>
                  <CheckCircle className="w-4 h-4 text-[var(--color-kids-success)]" />
                  <span className="text-[var(--color-kids-success)]">
                    Saved at {formatLastSaved(lastSaved)}
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 text-[var(--color-kids-success)]" />
                  <span className="text-[var(--color-kids-success)]">Saved</span>
                </>
              )}
            </div>

            {/* Export menu */}
            <ExportMenu
              project={projectData}
              disabled={readOnly}
              onExportComplete={handleExportComplete}
            />

            {/* Settings placeholder */}
            <Button variant="ghost" size="sm" onClick={() => showToast('Settings coming soon!', 'info')}>
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <div className="flex-1 flex flex-col max-w-6xl mx-auto w-full px-6 pt-6 pb-6">
        {/* Warnings - full width above columns */}
        {storageHealth.hasError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-[var(--color-kids-danger)]/10 border border-[var(--color-kids-danger)]/30 rounded-[var(--radius-kid)] flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-[var(--color-kids-danger)] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[var(--color-kids-danger)]">
                Trouble saving your work
              </p>
              <p className="text-xs text-[var(--color-kids-text-muted)]">
                Your device's storage may be full. Export your project now to avoid losing work.
              </p>
            </div>
            <button
              onClick={dismissStorageError}
              className="text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] text-sm"
            >
              Dismiss
            </button>
          </motion.div>
        )}

        {showExportReminder && !storageHealth.hasError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-[var(--color-kids-warning)]/10 border border-[var(--color-kids-warning)]/30 rounded-[var(--radius-kid)] flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-[var(--color-kids-warning)] flex-shrink-0" />
            <p className="flex-1 text-sm text-[var(--color-kids-text)]">
              Your work is only saved on this device. Export a backup to keep it safe!
            </p>
            <button
              onClick={() => setShowExportReminder(false)}
              className="text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] text-sm"
            >
              Dismiss
            </button>
          </motion.div>
        )}

        {/* Mobile tabs - visible only on mobile */}
        <div className="md:hidden flex gap-1 p-1 bg-[var(--color-kids-surface-alt)] rounded-[var(--radius-kid)] mb-4">
          <button
            onClick={() => setMobileTab('write')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-kid-sm)] text-sm font-medium transition-colors ${
              mobileTab === 'write'
                ? 'bg-white text-[var(--color-kids-primary)] shadow-sm'
                : 'text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)]'
            }`}
          >
            <FileText className="w-4 h-4" />
            Write
          </button>
          <button
            onClick={() => setMobileTab('chapters')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-kid-sm)] text-sm font-medium transition-colors ${
              mobileTab === 'chapters'
                ? 'bg-white text-[var(--color-kids-primary)] shadow-sm'
                : 'text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)]'
            }`}
          >
            <List className="w-4 h-4" />
            Chapters
          </button>
          <button
            onClick={() => setMobileTab('notes')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-kid-sm)] text-sm font-medium transition-colors ${
              mobileTab === 'notes'
                ? 'bg-white text-[var(--color-kids-primary)] shadow-sm'
                : 'text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)]'
            }`}
          >
            <StickyNote className="w-4 h-4" />
            Notes
          </button>
        </div>

        {/* 2-column layout */}
        <main className="flex-1 flex gap-6">
          {/* Sidebar - hidden on mobile */}
        <motion.aside
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden md:flex md:flex-col w-64 flex-shrink-0 gap-4"
        >
          {/* Chapters */}
          <Card className="flex-1 min-h-0">
            <ChapterList
              chapters={projectData.chapters}
              selectedChapterId={selectedChapterId}
              onSelect={handleChapterSelect}
              onAdd={handleAddChapter}
              onRename={handleRenameChapter}
              onDelete={handleDeleteChapter}
              onReorder={handleReorderChapters}
              disabled={readOnly}
            />
          </Card>

          {/* Notes */}
          <Card className="h-64 flex-shrink-0">
            <NotesTab
              notes={projectData.notes}
              onSave={handleSaveNotes}
              disabled={readOnly}
            />
          </Card>
        </motion.aside>

        {/* Mobile: Chapters tab content */}
        {mobileTab === 'chapters' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="md:hidden flex-1"
          >
            <Card className="h-full">
              <ChapterList
                chapters={projectData.chapters}
                selectedChapterId={selectedChapterId}
                onSelect={handleChapterSelect}
                onAdd={handleAddChapter}
                onRename={handleRenameChapter}
                onDelete={handleDeleteChapter}
                onReorder={handleReorderChapters}
                disabled={readOnly}
              />
            </Card>
          </motion.div>
        )}

        {/* Mobile: Notes tab content */}
        {mobileTab === 'notes' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="md:hidden flex-1"
          >
            <Card className="h-full">
              <NotesTab
                notes={projectData.notes}
                onSave={handleSaveNotes}
                disabled={readOnly}
              />
            </Card>
          </motion.div>
        )}

        {/* Editor panel - always visible on desktop, hidden on mobile when not on write tab */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex-1 flex flex-col min-w-0 ${mobileTab !== 'write' ? 'hidden md:flex' : ''}`}
        >
          <Card className="flex-1 flex flex-col">
            <CardContent className="flex-1 flex flex-col p-6">
              {/* Chapter header */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)]">
                    {activeChapter.title}
                  </h2>

                  {sortedChapters.length > 1 && (
                    <span className="text-xs text-[var(--color-kids-text-muted)]">
                      Chapter {sortedChapters.findIndex((c) => c.id === selectedChapterId) + 1} of {sortedChapters.length}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[var(--color-kids-text-muted)]">
                  Write your story here. It saves automatically!
                </p>
              </div>

              {/* Textarea */}
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={activeChapter.content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  placeholder="Once upon a time..."
                  className="
                    w-full h-full min-h-[400px] p-4
                    bg-[var(--color-kids-surface-alt)]
                    border-2 border-transparent
                    focus:border-[var(--color-kids-primary)]
                    rounded-[var(--radius-kid)]
                    font-body text-lg text-[var(--color-kids-text)]
                    placeholder:text-[var(--color-kids-text-muted)]
                    resize-none outline-none transition-colors
                  "
                  style={{ lineHeight: 1.8 }}
                />
              </div>

              {/* Footer stats */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--color-kids-border)]">
                <div className="flex items-center gap-6 text-sm text-[var(--color-kids-text-muted)]">
                  <span>
                    This chapter: <strong>{activeChapter.wordCount}</strong> words
                  </span>
                  <span>
                    Total project: <strong>{totalWordCount}</strong> words
                  </span>
                </div>

                {mode === 'local' && (
                  <p className="text-xs text-[var(--color-kids-text-light)]">
                    Saved to this device only
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
        </main>
      </div>
    </div>
  )
}
